import React, { useState } from 'react';
import {
  User, Briefcase, ArrowRight, ArrowLeft, CheckCircle,
  Building, MapPin, Mail, Phone, Stethoscope, Users,
  Calendar, Coffee, Heart, Search, X
} from 'lucide-react';
import { authService, supabase } from '../utils/supabase';
import { UnifiedProviderSearch } from '../utils/unifiedProviderSearch';
import PlaceholderCustomerService from '../utils/placeholderCustomerService';
import { staffService } from '../utils/supabaseStaffService';
import { locationService, customerLocationService } from '../services/locationService';
import { logger } from '../utils/logger';
import emailService from '../utils/emailService';
import referralService from '../services/referralService';

const OnboardingFlow = ({ currentUser, setCurrentView, setUserType, setCurrentUser, onOnboardingComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState({
    userType: '',
    title: '',
    company: '',
    location: '', // Deprecated - replaced by structured location fields below
    phone: '',
    specialty: '',
    npi_number: '',
    territory: '',
    products: [],
    delegating_for: null, // For office staff - ID of physician they manage
    dietaryRestrictions: [],
    cuisinePreferences: [],
    meetingPreferences: {
      preferredDays: [],
      preferredTimes: [],
      budgetRange: '',
      meetingDuration: '60'
    },
    // Primary Location (for customers only)
    primaryLocation: {
      location_name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      location_type: 'office' // Default to 'office'
    },
    // Working days at primary location (for customers only)
    workingDays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    }
  });

  // Physician search state (for office staff)
  const [physicianSearchQuery, setPhysicianSearchQuery] = useState('');
  const [physicianSearchResults, setPhysicianSearchResults] = useState([]);
  const [isSearchingPhysician, setIsSearchingPhysician] = useState(false);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualPhysicianData, setManualPhysicianData] = useState({
    firstName: '',
    lastName: ''
  });
  const [pendingInvitation, setPendingInvitation] = useState(null);
  const [isCheckingInvitation, setIsCheckingInvitation] = useState(false);

  // Check for pending invitations when office staff user type is selected
  React.useEffect(() => {
    const checkForPendingInvitation = async () => {
      // Only check when user selects office_staff and has email
      if (onboardingData.userType !== 'office_staff' || !currentUser?.email) {
        return;
      }

      setIsCheckingInvitation(true);
      try {
        logger.debug('Checking for pending invitation', { email: currentUser.email });
        const invitation = await staffService.getPendingInvitationByEmail(currentUser.email);

        if (invitation) {
          logger.debug('Found pending invitation', {
            physicianId: invitation.customer_id,
            physicianName: invitation.physician ? `${invitation.physician.first_name} ${invitation.physician.last_name}` : 'Unknown'
          });

          setPendingInvitation(invitation);

          // Auto-select the physician from the invitation
          const physicianData = invitation.physician;
          if (physicianData) {
            const autoSelectedPhysician = {
              id: physicianData.id,
              name: `${physicianData.first_name || ''} ${physicianData.last_name || ''}`.trim(),
              company: physicianData.company,
              specialty: physicianData.specialty,
              email: physicianData.email,
              source: 'registered',
              isInvited: true
            };

            handleSelectPhysician(autoSelectedPhysician);
          }
        } else {
          logger.debug('No pending invitation found');
          setPendingInvitation(null);
        }
      } catch (error) {
        logger.error('Error checking for pending invitation', { error: error.message });
        setPendingInvitation(null);
      } finally {
        setIsCheckingInvitation(false);
      }
    };

    checkForPendingInvitation();
  }, [onboardingData.userType, currentUser?.email]);

  // Dynamic total steps based on user type
  const getTotalSteps = () => {
    if (onboardingData.userType === 'sales') return 3;
    if (onboardingData.userType === 'manager') return 3;
    if (onboardingData.userType === 'office_staff') return 4; // User type → Physician search → Profile → Location
    return 4; // customer: User type → Profile → Location → Preferences
  };

  const userTypes = [
    {
      id: 'customer',
      title: 'Healthcare Provider',
      description: 'Doctor, Nurse, or Medical Professional',
      icon: <Stethoscope className="w-8 h-8" />,
      color: 'blue'
    },
    {
      id: 'office_staff',
      title: 'Office Manager/Staff',
      description: 'Managing meetings on behalf of healthcare provider',
      icon: <Users className="w-8 h-8" />,
      color: 'green'
    },
    {
      id: 'sales',
      title: 'Sales Representative', 
      description: 'Pharmaceutical or Medical Device Sales',
      icon: <Briefcase className="w-8 h-8" />,
      color: 'green'
    },
    {
      id: 'manager',
      title: 'Sales Manager',
      description: 'Regional or District Sales Manager',
      icon: <Users className="w-8 h-8" />,
      color: 'purple'
    }
  ];

  const specialties = [
    'Cardiology', 'Orthopedics', 'Neurology', 'Pediatrics', 
    'Internal Medicine', 'Oncology', 'Dermatology', 'Psychiatry',
    'Emergency Medicine', 'Family Medicine', 'Anesthesiology'
  ];

  const cuisineOptions = [
    'Italian', 'Mediterranean', 'Japanese', 'Chinese', 'Mexican', 
    'American', 'Indian', 'Thai', 'French', 'Greek', 'Korean', 'Vietnamese'
  ];

  const dietaryOptions = [
    'Vegetarian', 'Vegan', 'Gluten-free', 'Dairy-free', 'Keto', 
    'Paleo', 'Low-sodium', 'Diabetic-friendly', 'Heart-healthy'
  ];

  const handleNext = () => {
    // Validate Step 3 (Location) for customers before proceeding
    const isCustomerLocationStep = currentStep === 3 && onboardingData.userType === 'customer';
    // Validate Step 4 (Location) for office_staff creating placeholder physician
    const isOfficeStaffLocationStep = currentStep === 4 && onboardingData.userType === 'office_staff' &&
                                       (selectedPhysician?.source === 'manual' || selectedPhysician?.source === 'npi');

    if (isCustomerLocationStep || isOfficeStaffLocationStep) {
      const { location_name, address, city, state, zip } = onboardingData.primaryLocation;

      if (!location_name || !location_name.trim()) {
        const entityName = onboardingData.userType === 'customer' ? 'your practice' : 'the physician\'s practice';
        alert(`Please enter a location name for ${entityName}.`);
        return;
      }
      if (!address || !address.trim()) {
        const entityName = onboardingData.userType === 'customer' ? 'your practice' : 'the physician\'s practice';
        alert(`Please enter a street address for ${entityName}.`);
        return;
      }
      if (!city || !city.trim()) {
        const entityName = onboardingData.userType === 'customer' ? 'your practice' : 'the physician\'s practice';
        alert(`Please enter a city for ${entityName}.`);
        return;
      }
      if (!state) {
        const entityName = onboardingData.userType === 'customer' ? 'your practice' : 'the physician\'s practice';
        alert(`Please select a state for ${entityName}.`);
        return;
      }
      if (!zip || !zip.trim()) {
        const entityName = onboardingData.userType === 'customer' ? 'your practice' : 'the physician\'s practice';
        alert(`Please enter a ZIP code for ${entityName}.`);
        return;
      }

      // Validate at least one working day is selected
      const hasWorkingDay = Object.values(onboardingData.workingDays).some(day => day === true);
      if (!hasWorkingDay) {
        const entityName = onboardingData.userType === 'customer' ? 'your' : 'the physician\'s';
        alert(`Please select at least one working day at ${entityName} location.`);
        return;
      }

      // For office_staff creating placeholder, add location to placeholderCustomerData
      if (isOfficeStaffLocationStep) {
        // Build the complete data object with location info included
        // We pass this directly to completeOnboarding because setOnboardingData is async
        const completeData = {
          ...onboardingData,
          placeholderCustomerData: {
            ...onboardingData.placeholderCustomerData,
            primaryLocation: onboardingData.primaryLocation,
            workingDays: onboardingData.workingDays
          }
        };
        setOnboardingData(completeData); // Still update state for consistency

        // If this is the final step, pass data directly to avoid async state issue
        const maxSteps = getTotalSteps();
        if (currentStep >= maxSteps) {
          completeOnboarding(completeData);
          return;
        }
      }
    }

    const maxSteps = getTotalSteps();
    if (currentStep < maxSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Physician search for office staff
  const handlePhysicianSearch = async () => {
    if (!physicianSearchQuery.trim()) {
      setPhysicianSearchResults([]);
      return;
    }

    setIsSearchingPhysician(true);
    try {
      logger.debug('Searching for physicians', { query: physicianSearchQuery });
      const results = await UnifiedProviderSearch.searchProviders({
        name: physicianSearchQuery,
        specialty: '',
        location: '',
        limit: 10
      });

      logger.debug('Physician search results', { count: results.providers?.length });
      setPhysicianSearchResults(results.providers || []);
    } catch (error) {
      logger.error('Error searching physicians', { error: error.message });
      setPhysicianSearchResults([]);
    } finally {
      setIsSearchingPhysician(false);
    }
  };

  const handleSelectPhysician = (physician) => {
    setSelectedPhysician(physician);

    // Check if this is an NPI-only physician (not registered in RepConnect)
    const isNPIOnly = physician.source === 'npi' && !physician.isRegistered;

    // Pre-populate profile fields from physician data
    const updatedData = {
      ...onboardingData
    };

    // Handle NPI-only physicians (need to create placeholder)
    if (isNPIOnly) {
      updatedData.delegating_for = 'pending_creation';

      // Parse name intelligently (handle "Dr. FirstName LastName" format)
      let firstName = '';
      let lastName = '';
      if (physician.name) {
        const nameParts = physician.name.trim().split(/\s+/);
        // Remove common titles
        const filteredParts = nameParts.filter(part =>
          !['Dr.', 'Dr', 'MD', 'DO', 'NP', 'PA'].includes(part)
        );
        if (filteredParts.length > 0) {
          firstName = filteredParts[0];
          lastName = filteredParts.slice(1).join(' ') || filteredParts[0];
        }
      }

      updatedData.placeholderCustomerData = {
        firstName: firstName || 'Unknown',
        lastName: lastName || 'Physician',
        name: physician.name,
        npi_number: physician.npi_number || physician.npi,
        specialty: physician.specialty,
        company: physician.hospital,
        phone: physician.phone,
        location: physician.location,
        source: 'npi'
      };
      logger.debug('NPI-only physician selected, will create placeholder', {
        npi: physician.npi_number || physician.npi,
        name: physician.name
      });
    } else {
      // Registered physician - use their existing UUID
      updatedData.delegating_for = physician.id;
      logger.debug('Registered physician selected', {
        physicianId: physician.id,
        physicianName: physician.name
      });
    }

    // If physician has profile data, pre-populate it (user can still edit)
    if (physician.company) {
      updatedData.company = physician.company;
    }
    if (physician.specialty) {
      updatedData.specialty = physician.specialty;
    }
    if (physician.location) {
      updatedData.location = physician.location;
    }
    if (physician.phone) {
      updatedData.phone = physician.phone;
    }

    setOnboardingData(updatedData);
    logger.debug('Selected physician and pre-populated fields', {
      physicianId: physician.id,
      physicianName: physician.name,
      isNPIOnly,
      preFilled: { company: physician.company, specialty: physician.specialty }
    });
  };

  const handleSkipPhysicianLink = () => {
    setSelectedPhysician(null);
    setOnboardingData({ ...onboardingData, delegating_for: null });
    logger.debug('Skipped physician linking');
  };

  const handleManualPhysicianEntry = () => {
    if (!manualPhysicianData.firstName.trim() || !manualPhysicianData.lastName.trim()) {
      return;
    }

    // Create a placeholder physician object from manual entry
    const manualPhysician = {
      id: null, // Will be created during onboarding completion
      name: `${manualPhysicianData.firstName} ${manualPhysicianData.lastName}`,
      firstName: manualPhysicianData.firstName,
      lastName: manualPhysicianData.lastName,
      source: 'manual',
      isPlaceholder: true
    };

    setSelectedPhysician(manualPhysician);
    setOnboardingData({
      ...onboardingData,
      delegating_for: 'pending_creation', // Special flag to create placeholder customer
      placeholderCustomerData: manualPhysician
    });
    setShowManualEntry(false);
    logger.debug('Manual physician entry', { name: manualPhysician.name });
  };

  const completeOnboarding = async (dataOverride = null) => {
    // For office staff location step, dataOverride contains the complete data with location info
    // This bypasses React's async state update issue
    const effectiveData = dataOverride || onboardingData;

    try {
      logger.debug('🚀 Completing onboarding...', { userId: currentUser.id, onboardingData: effectiveData });

      let finalDelegatingFor = onboardingData.delegating_for;

      // Check for duplicate/placeholder customer accounts (for physicians signing up)
      if (onboardingData.userType === 'customer') {
        try {
          const searchCriteria = {
            firstName: currentUser.first_name || onboardingData.firstName,
            lastName: currentUser.last_name || onboardingData.lastName,
            npi_number: onboardingData.npi_number,
            email: currentUser.email,
            excludeUserId: currentUser.id // Exclude current user from search
          };

          logger.debug('Checking for duplicate/placeholder customer accounts', searchCriteria);

          const existingCustomer = await PlaceholderCustomerService.findExistingCustomer(searchCriteria);

          if (existingCustomer && existingCustomer.id !== currentUser.id) {
            // Found a potential duplicate/placeholder
            const createdByStaff = existingCustomer.created_by;
            const isPendingActivation = existingCustomer.account_status === 'pending_activation';

            if (isPendingActivation && createdByStaff) {
              // This is a placeholder created by staff - offer to claim it
              const confirmMessage = `An account already exists with this ${
                onboardingData.npi_number ? 'NPI number' : 'name'
              }. It was set up by your office staff. Would you like to claim this account and keep your staff's access?`;

              const shouldMerge = window.confirm(confirmMessage);

              if (shouldMerge) {
                logger.debug('User confirmed placeholder claim', {
                  placeholderId: existingCustomer.id,
                  newUserId: currentUser.id
                });

                try {
                  // Call API to properly merge the accounts
                  // This will:
                  // 1. Update placeholder auth user with physician's credentials
                  // 2. Merge physician's data into placeholder profile
                  // 3. Delete physician's temporary auth user
                  // 4. Preserve admin relationships
                  const mergedProfile = await PlaceholderCustomerService.claimPlaceholderAccount(
                    existingCustomer.id, // Placeholder ID to keep
                    currentUser.id, // New user ID to delete
                    {
                      firstName: onboardingData.firstName || currentUser.first_name,
                      lastName: onboardingData.lastName || currentUser.last_name,
                      email: currentUser.email,
                      phone: onboardingData.phone,
                      specialty: onboardingData.specialty,
                      npi_number: onboardingData.npi_number,
                      company: onboardingData.company,
                      street_address: onboardingData.street_address,
                      city: onboardingData.city,
                      state: onboardingData.state,
                      zip: onboardingData.zip
                    }
                  );

                  logger.debug('✅ Placeholder account claimed successfully', {
                    profileId: mergedProfile.id
                  });

                  // Update local state with merged profile
                  setCurrentUser({ ...currentUser, ...mergedProfile, id: mergedProfile.id });
                  setUserType(mergedProfile.user_type);

                  // Sign user in with the placeholder account credentials
                  // (The API already updated the auth user's email)
                  // We need to refresh the session
                  const { data: sessionData, error: sessionError } = await supabase.auth.refreshSession();
                  if (sessionError) {
                    logger.warn('Session refresh failed, user may need to re-login', { error: sessionError.message });
                  }

                  if (onOnboardingComplete) {
                    onOnboardingComplete({ ...currentUser, ...mergedProfile, id: mergedProfile.id });
                  }

                  setCurrentView('dashboard');
                  return; // Exit early - account claimed

                } catch (error) {
                  logger.error('Failed to claim placeholder account', { error: error.message });
                  alert('There was an error claiming your account. Please contact support or try creating a new account.');
                  return;
                }
              } else {
                logger.debug('User declined placeholder claim, proceeding with new profile');
              }
            }
          }
        } catch (error) {
          logger.error('Error checking for duplicate customer', { error: error.message });
          // Continue with normal onboarding if duplicate check fails
        }
      }

      // Handle placeholder customer creation for office staff
      if (onboardingData.userType === 'office_staff') {
        // Case 0: Activate pending invitation if exists
        if (pendingInvitation) {
          logger.debug('Activating pending invitation', { invitationId: pendingInvitation.id });
          try {
            await staffService.activatePendingInvitation(pendingInvitation.id, currentUser.id);
            finalDelegatingFor = pendingInvitation.customer_id;
            logger.debug('Pending invitation activated', { customerId: finalDelegatingFor });
          } catch (error) {
            logger.error('Failed to activate pending invitation', { error: error.message });
            // Continue with normal flow if activation fails
          }
        }
        // Case 1: Manual entry or NPI-only physician (needs placeholder creation)
        // Use effectiveData here - it contains location data passed directly from handleNext
        else if (effectiveData.delegating_for === 'pending_creation' && effectiveData.placeholderCustomerData) {
          logger.debug('Creating placeholder customer for manual/NPI entry');

          try {
            const placeholderCustomer = await PlaceholderCustomerService.createPlaceholderCustomer(
              effectiveData.placeholderCustomerData,
              currentUser.id
            );

            finalDelegatingFor = placeholderCustomer.id;
            logger.debug('Placeholder customer created', { customerId: placeholderCustomer.id });

            // Store location data to create AFTER admin relationship is established
            // (RLS policy requires admin_users record to exist first)

          } catch (error) {
            logger.error('Failed to create placeholder customer', { error: error.message });
            // Don't throw - allow staff onboarding to complete even if physician link fails
            // They can link the physician later from their dashboard
            finalDelegatingFor = null;
            logger.warn('Continuing staff onboarding without physician link - can be added later');

            // Show a warning to the user
            alert('Note: Your account was created successfully, but we couldn\'t link the physician profile. You can add them later from your dashboard.');
          }
        }

        // Case 2: Selected existing physician from Supabase (already has ID)
        // finalDelegatingFor already set from selectedPhysician.id

        // Link staff as admin to the customer (only if no pending invitation - that's already linked)
        if (finalDelegatingFor && finalDelegatingFor !== 'pending_creation' && !pendingInvitation) {
          try {
            await PlaceholderCustomerService.linkStaffAsAdmin(
              finalDelegatingFor,
              currentUser.id,
              'office_manager'
            );
            logger.debug('Staff linked as admin', { customerId: finalDelegatingFor });
          } catch (error) {
            logger.error('Failed to link staff as admin', { error: error.message });
            // Don't fail onboarding if admin link fails - can be done later
            logger.warn('Continuing onboarding despite admin link failure');
          }
        }

        // NOW create the location AFTER admin relationship exists (RLS policy check will pass)
        if (finalDelegatingFor && finalDelegatingFor !== 'pending_creation') {
          logger.debug('Checking location data for placeholder:', {
            hasLocationName: !!effectiveData.primaryLocation?.location_name,
            locationName: effectiveData.primaryLocation?.location_name,
            hasWorkingDays: !!effectiveData.workingDays,
            primaryLocation: effectiveData.primaryLocation
          });

          if (effectiveData.primaryLocation?.location_name &&
              effectiveData.workingDays) {
            try {
              logger.debug('Creating primary practice location for placeholder physician', {
                physicianId: finalDelegatingFor,
                locationData: effectiveData.primaryLocation
              });

              // Step 1: Create the practice location
              const createdLocation = await locationService.createLocation({
                location_name: effectiveData.primaryLocation.location_name.trim(),
                address: effectiveData.primaryLocation.address.trim(),
                city: effectiveData.primaryLocation.city.trim(),
                state: effectiveData.primaryLocation.state,
                zip: effectiveData.primaryLocation.zip.trim(),
                phone: effectiveData.primaryLocation.phone?.trim() || null,
                location_type: 'office'
              });

              logger.debug('✅ Practice location created for placeholder physician:', { locationId: createdLocation.id });

              // Step 2: Link placeholder physician to the location
              const customerLocationLink = await customerLocationService.linkCustomerToLocation({
                customer_id: finalDelegatingFor,
                location_id: createdLocation.id,
                is_primary_location: true,
                works_monday: effectiveData.workingDays.monday,
                works_tuesday: effectiveData.workingDays.tuesday,
                works_wednesday: effectiveData.workingDays.wednesday,
                works_thursday: effectiveData.workingDays.thursday,
                works_friday: effectiveData.workingDays.friday,
                works_saturday: effectiveData.workingDays.saturday,
                works_sunday: effectiveData.workingDays.sunday,
                role_at_location: effectiveData.placeholderCustomerData?.title || 'Physician',
                department: null
              });

              logger.debug('✅ Placeholder physician linked to primary location:', {
                customerLocationId: customerLocationLink.id,
                isPrimary: true
              });

            } catch (error) {
              logger.error('❌ Failed to create primary location for placeholder physician:', error);
              // Don't block onboarding - location can be added later
              logger.warn('Placeholder physician created successfully, but location creation failed - can be added later');
            }
          }
        }
      }

      // Update user profile in Supabase
      const updatedProfile = await authService.completeOnboarding(currentUser.id, {
        user_type: onboardingData.userType,
        title: onboardingData.title,
        phone: onboardingData.phone,
        company: onboardingData.company,
        territory: onboardingData.territory,
        specialty: onboardingData.specialty,
        npi_number: onboardingData.npi_number,
        delegating_for: finalDelegatingFor, // Link to physician for office staff
        email: currentUser.email // Include email for profile creation if needed
      });

      logger.debug('✅ Profile updated:', updatedProfile);

      // Create primary location for customers
      if (onboardingData.userType === 'customer') {
        try {
          logger.debug('Creating primary practice location for customer', {
            customerId: currentUser.id,
            locationData: onboardingData.primaryLocation
          });

          // Step 1: Create the practice location
          const createdLocation = await locationService.createLocation({
            location_name: onboardingData.primaryLocation.location_name.trim(),
            address: onboardingData.primaryLocation.address.trim(),
            city: onboardingData.primaryLocation.city.trim(),
            state: onboardingData.primaryLocation.state,
            zip: onboardingData.primaryLocation.zip.trim(),
            phone: onboardingData.primaryLocation.phone?.trim() || null,
            location_type: 'office'
          });

          logger.debug('✅ Practice location created:', { locationId: createdLocation.id });

          // Step 2: Link customer to the location
          const customerLocationLink = await customerLocationService.linkCustomerToLocation({
            customer_id: currentUser.id,
            location_id: createdLocation.id,
            is_primary_location: true,
            works_monday: onboardingData.workingDays.monday,
            works_tuesday: onboardingData.workingDays.tuesday,
            works_wednesday: onboardingData.workingDays.wednesday,
            works_thursday: onboardingData.workingDays.thursday,
            works_friday: onboardingData.workingDays.friday,
            works_saturday: onboardingData.workingDays.saturday,
            works_sunday: onboardingData.workingDays.sunday,
            role_at_location: onboardingData.title || 'Physician',
            department: null
          });

          logger.debug('✅ Customer linked to primary location:', {
            customerLocationId: customerLocationLink.id,
            isPrimary: true
          });

        } catch (error) {
          logger.error('❌ Failed to create primary location:', error);
          // Show error to user but don't block onboarding completion
          // They can add location later from their profile
          alert('Your profile was created successfully, but we couldn\'t save your location. You can add it later from your profile settings.');
        }
      }

      // Update current user state
      const updatedUser = { ...currentUser, ...updatedProfile };
      setCurrentUser(updatedUser);
      setUserType(updatedProfile.user_type);

      // Send welcome email (don't block on failure)
      try {
        logger.debug('📧 Sending welcome email...');
        await emailService.sendWelcomeEmail({
          email: currentUser.email,
          firstName: currentUser.first_name || onboardingData.firstName,
          lastName: currentUser.last_name || onboardingData.lastName,
          userType: updatedProfile.user_type
        });
        logger.debug('✅ Welcome email sent successfully');
      } catch (emailError) {
        // Log but don't block - email is non-critical
        logger.error('Failed to send welcome email:', emailError);
      }

      // Check for and convert referral (for customers who were invited by sales reps)
      if (updatedProfile.user_type === 'customer' || updatedProfile.user_type === 'office_staff') {
        try {
          logger.debug('🔗 Checking for referral conversion...');
          const referralResult = await referralService.convertReferral(
            currentUser.email,
            currentUser.id
          );
          if (referralResult?.was_converted) {
            logger.debug('✅ Referral converted!', {
              referrerId: referralResult.referrer_id,
              referrerName: referralResult.referrer_name
            });
          }
        } catch (referralError) {
          // Log but don't block - referral conversion is non-critical
          logger.error('Failed to convert referral:', referralError);
        }
      }

      // Call onboarding completion callback
      if (onOnboardingComplete) {
        onOnboardingComplete(updatedUser);
      }

      logger.debug('🏠 Redirecting to dashboard...');

      // Force page reload to ensure all state (delegation context, locations, etc.)
      // is properly loaded from the database. This prevents race conditions where
      // the dashboard renders before async state updates have propagated.
      window.location.href = '/';
      return;

    } catch (error) {
      logger.error('❌ Onboarding completion failed:', error);
      alert(`Failed to complete onboarding: ${error.message || 'Please try again.'}`);
    }
  };

  const canProceed = () => {
    // Handle office_staff flow separately (has physician search as step 2)
    if (onboardingData.userType === 'office_staff') {
      switch (currentStep) {
        case 1:
          return onboardingData.userType !== '';
        case 2:
          return true; // Physician search is optional - they can skip
        case 3:
          // Profile details (same as step 2 for non-office_staff)
          return onboardingData.title && onboardingData.company;
        case 4:
          // Location step - if creating placeholder physician, require full location
          if (selectedPhysician?.source === 'manual' || selectedPhysician?.source === 'npi') {
            const { location_name, address, city, state, zip } = onboardingData.primaryLocation;
            const hasWorkingDay = Object.values(onboardingData.workingDays).some(day => day === true);
            return location_name && address && city && state && zip && hasWorkingDay && onboardingData.phone;
          }
          // For existing physicians or no physician selected, just require phone and simple location
          return onboardingData.phone && onboardingData.location;
        default:
          return false;
      }
    }

    // Handle other user types (sales, manager, customer)
    switch (currentStep) {
      case 1:
        return onboardingData.userType !== '';
      case 2:
        if (onboardingData.userType === 'customer') {
          return onboardingData.title && onboardingData.company && onboardingData.specialty;
        } else {
          return onboardingData.title && onboardingData.company && onboardingData.territory;
        }
      case 3:
        // For customers, validate the structured primaryLocation fields
        if (onboardingData.userType === 'customer') {
          const { location_name, address, city, state, zip } = onboardingData.primaryLocation;
          const hasWorkingDay = Object.values(onboardingData.workingDays).some(day => day === true);
          return onboardingData.phone && location_name && address && city && state && zip && hasWorkingDay;
        }
        // For sales/manager, use simple location field
        return onboardingData.phone && onboardingData.location;
      case 4:
        return true; // Optional preferences (only for healthcare providers)
      default:
        return false;
    }
  };

  const toggleArrayItem = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    } else {
      return [...array, item];
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome to RepConnect!</h2>
        <p className="text-gray-600">Let's get you set up. What best describes your role?</p>
      </div>

      <div className="space-y-4">
        {userTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setOnboardingData({...onboardingData, userType: type.id})}
            className={`w-full p-6 border-2 rounded-lg transition-all ${
              onboardingData.userType === type.id
                ? `border-${type.color}-500 bg-${type.color}-50`
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-full ${
                onboardingData.userType === type.id 
                  ? `bg-${type.color}-100 text-${type.color}-600`
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {type.icon}
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-800">{type.title}</h3>
                <p className="text-gray-600">{type.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Physician search step for office staff (Step 1.5)
  const renderPhysicianSearchStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Who do you work for?</h2>
        <p className="text-gray-600">Search for the healthcare provider you manage meetings for</p>
      </div>

      {/* Invitation Notice */}
      {isCheckingInvitation && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 text-center">Checking for invitations...</p>
        </div>
      )}

      {pendingInvitation && selectedPhysician && (
        <div className="p-4 bg-green-50 border-2 border-green-300 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-800 mb-1">You've been invited!</p>
              <p className="text-sm text-green-700">
                {selectedPhysician.name} invited you to manage their RepConnect portal.
                We've automatically linked your account.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search Box */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by physician name, specialty, or location..."
            value={physicianSearchQuery}
            onChange={(e) => setPhysicianSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handlePhysicianSearch()}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handlePhysicianSearch}
          disabled={isSearchingPhysician || !physicianSearchQuery.trim()}
          className="mt-3 w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isSearchingPhysician ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Selected Physician */}
      {selectedPhysician && (
        <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="font-semibold text-green-800">{selectedPhysician.name}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-green-700">
                {selectedPhysician.specialty && (
                  <span>{selectedPhysician.specialty}</span>
                )}
                {selectedPhysician.location && (
                  <span>• {selectedPhysician.location}</span>
                )}
              </div>
              {selectedPhysician.npi_number && (
                <p className="text-xs text-green-600 font-mono mt-1">
                  NPI: {selectedPhysician.npi_number}
                </p>
              )}
            </div>
            <button
              onClick={() => {
                setSelectedPhysician(null);
                setOnboardingData({ ...onboardingData, delegating_for: null });
              }}
              className="text-green-600 hover:text-green-800 flex-shrink-0 ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Search Results */}
      {!selectedPhysician && physicianSearchResults.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          <p className="text-sm text-gray-600 mb-2">
            Found {physicianSearchResults.length} provider{physicianSearchResults.length !== 1 ? 's' : ''}
          </p>
          {physicianSearchResults.map((physician) => (
            <button
              key={physician.id}
              onClick={() => handleSelectPhysician(physician)}
              className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800">{physician.name}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600">
                    {physician.specialty && (
                      <span className="flex items-center">
                        <Stethoscope className="w-3 h-3 mr-1 flex-shrink-0" />
                        {physician.specialty}
                      </span>
                    )}
                    {physician.location && (
                      <span className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                        {physician.location}
                      </span>
                    )}
                    {physician.npi_number && (
                      <span className="flex items-center text-blue-600 font-mono text-xs">
                        NPI: {physician.npi_number}
                      </span>
                    )}
                  </div>
                  {physician.source && (
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full ${
                      physician.source === 'registered'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {physician.source === 'registered' ? 'Registered' : 'NPI Database'}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {!selectedPhysician && physicianSearchQuery && physicianSearchResults.length === 0 && !isSearchingPhysician && (
        <div className="text-center p-6 bg-gray-50 rounded-lg">
          <p className="text-gray-600 mb-3">No providers found matching your search</p>
        </div>
      )}

      {/* Manual Entry or Skip Option */}
      {!selectedPhysician && (
        <div className="pt-4 border-t border-gray-200">
          {!showManualEntry ? (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">Can't find your provider?</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button
                  onClick={() => setShowManualEntry(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Enter Name Manually
                </button>
                <button
                  onClick={handleSkipPhysicianLink}
                  className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium"
                >
                  I'll link them later
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="font-semibold text-gray-800 mb-1">Enter Physician Name</h3>
                <p className="text-sm text-gray-600">We'll create a profile that they can claim later</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    placeholder="John"
                    value={manualPhysicianData.firstName}
                    onChange={(e) => setManualPhysicianData({
                      ...manualPhysicianData,
                      firstName: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Smith"
                    value={manualPhysicianData.lastName}
                    onChange={(e) => setManualPhysicianData({
                      ...manualPhysicianData,
                      lastName: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleManualPhysicianEntry}
                  disabled={!manualPhysicianData.firstName.trim() || !manualPhysicianData.lastName.trim()}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Create Profile
                </button>
                <button
                  onClick={() => {
                    setShowManualEntry(false);
                    setManualPhysicianData({ firstName: '', lastName: '' });
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Professional Information</h2>
        <p className="text-gray-600">Tell us about your professional background</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Professional Title
          </label>
          <input
            type="text"
            value={onboardingData.title}
            onChange={(e) => setOnboardingData({...onboardingData, title: e.target.value})}
            placeholder={onboardingData.userType === 'customer' ? 'e.g., Cardiologist, Nurse Practitioner' : 'e.g., Senior Sales Representative, Territory Manager'}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {onboardingData.userType === 'customer' ? 'Hospital/Practice' : 'Company'}
          </label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={onboardingData.company}
              onChange={(e) => setOnboardingData({...onboardingData, company: e.target.value})}
              placeholder={onboardingData.userType === 'customer' ? 'Memorial Hospital' : 'PharmaCorp Inc.'}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {onboardingData.userType === 'customer' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Medical Specialty
            </label>
            <select
              value={onboardingData.specialty}
              onChange={(e) => setOnboardingData({...onboardingData, specialty: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select your specialty</option>
              {specialties.map((specialty) => (
                <option key={specialty} value={specialty}>{specialty}</option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {onboardingData.userType === 'manager' ? 'Management Region' : 'Territory/Region'}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={onboardingData.territory}
                onChange={(e) => setOnboardingData({...onboardingData, territory: e.target.value})}
                placeholder={onboardingData.userType === 'manager' 
                  ? "e.g., Western Region, District 5" 
                  : "e.g., Northern California, Northeast Region"
                }
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {onboardingData.userType === 'manager' && (
              <p className="text-xs text-gray-500 mt-1">
                This is the region you'll manage. Sales representatives in this region can be assigned to your team.
              </p>
            )}
          </div>
        )}

        {onboardingData.userType === 'customer' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              NPI Number (Optional)
            </label>
            <input
              type="text"
              value={onboardingData.npi_number}
              onChange={(e) => setOnboardingData({...onboardingData, npi_number: e.target.value})}
              placeholder="1234567890"
              maxLength="10"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your 10-digit National Provider Identifier
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Contact Information</h2>
        <p className="text-gray-600">How can people reach you?</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="tel"
              value={onboardingData.phone}
              onChange={(e) => setOnboardingData({...onboardingData, phone: e.target.value})}
              placeholder="(555) 123-4567"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Location fields - structured for customers and office_staff creating physicians, simple for sales/manager */}
        {(onboardingData.userType === 'customer' ||
          (onboardingData.userType === 'office_staff' && (selectedPhysician?.source === 'manual' || selectedPhysician?.source === 'npi'))) ? (
          // Customer OR Office staff creating placeholder: Collect complete primary location details
          <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Building className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">
                {onboardingData.userType === 'customer'
                  ? 'Primary Practice Location'
                  : `${selectedPhysician?.name || 'Physician'}'s Primary Practice Location`}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {onboardingData.userType === 'customer'
                ? 'This will be your main location. You can add additional locations later.'
                : 'This will be their main practice location. Additional locations can be added later.'}
            </p>

            {/* Location Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={onboardingData.primaryLocation.location_name}
                onChange={(e) => setOnboardingData({
                  ...onboardingData,
                  primaryLocation: { ...onboardingData.primaryLocation, location_name: e.target.value }
                })}
                placeholder="e.g., Main Office, Downtown Clinic"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Street Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={onboardingData.primaryLocation.address}
                  onChange={(e) => setOnboardingData({
                    ...onboardingData,
                    primaryLocation: { ...onboardingData.primaryLocation, address: e.target.value }
                  })}
                  placeholder="Suite 100, 123 Medical Plaza Dr"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* City, State, ZIP */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={onboardingData.primaryLocation.city}
                  onChange={(e) => setOnboardingData({
                    ...onboardingData,
                    primaryLocation: { ...onboardingData.primaryLocation, city: e.target.value }
                  })}
                  placeholder="San Francisco"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={onboardingData.primaryLocation.state}
                  onChange={(e) => setOnboardingData({
                    ...onboardingData,
                    primaryLocation: { ...onboardingData.primaryLocation, state: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select State</option>
                  <option value="CA">CA</option>
                  <option value="NY">NY</option>
                  <option value="TX">TX</option>
                  <option value="FL">FL</option>
                  <option value="IL">IL</option>
                  <option value="PA">PA</option>
                  <option value="OH">OH</option>
                  <option value="GA">GA</option>
                  <option value="NC">NC</option>
                  <option value="MI">MI</option>
                  {/* Add more states as needed */}
                </select>
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={onboardingData.primaryLocation.zip}
                  onChange={(e) => setOnboardingData({
                    ...onboardingData,
                    primaryLocation: { ...onboardingData.primaryLocation, zip: e.target.value }
                  })}
                  placeholder="94102"
                  maxLength="10"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* Office Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Office Phone (Optional)
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="tel"
                  value={onboardingData.primaryLocation.phone}
                  onChange={(e) => setOnboardingData({
                    ...onboardingData,
                    primaryLocation: { ...onboardingData.primaryLocation, phone: e.target.value }
                  })}
                  placeholder="(555) 123-4567"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Working Days */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Calendar className="inline w-4 h-4 mr-2" />
                Which days do you work at this location?
              </label>
              <div className="grid grid-cols-7 gap-2">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setOnboardingData({
                      ...onboardingData,
                      workingDays: { ...onboardingData.workingDays, [day]: !onboardingData.workingDays[day] }
                    })}
                    className={`p-2 text-xs rounded-lg border transition-colors ${
                      onboardingData.workingDays[day]
                        ? 'bg-blue-500 border-blue-600 text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {day.substring(0, 3).toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          // Sales/Staff: Simple location field
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Work Location
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={onboardingData.location}
                onChange={(e) => setOnboardingData({...onboardingData, location: e.target.value})}
                placeholder="San Francisco Bay Area"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Preferences</h2>
        <p className="text-gray-600">Customize your meeting experience (optional)</p>
      </div>

      <div className="space-y-6">
        {/* Cuisine Preferences */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Coffee className="inline w-4 h-4 mr-2" />
            Preferred Cuisines
          </label>
          <div className="grid grid-cols-2 gap-2">
            {cuisineOptions.map((cuisine) => (
              <button
                key={cuisine}
                onClick={() => setOnboardingData({
                  ...onboardingData,
                  cuisinePreferences: toggleArrayItem(onboardingData.cuisinePreferences, cuisine)
                })}
                className={`p-2 text-sm rounded-lg border transition-colors ${
                  onboardingData.cuisinePreferences.includes(cuisine)
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {cuisine}
              </button>
            ))}
          </div>
        </div>

        {/* Dietary Restrictions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Heart className="inline w-4 h-4 mr-2" />
            Dietary Restrictions
          </label>
          <div className="grid grid-cols-2 gap-2">
            {dietaryOptions.map((diet) => (
              <button
                key={diet}
                onClick={() => setOnboardingData({
                  ...onboardingData,
                  dietaryRestrictions: toggleArrayItem(onboardingData.dietaryRestrictions, diet)
                })}
                className={`p-2 text-sm rounded-lg border transition-colors ${
                  onboardingData.dietaryRestrictions.includes(diet)
                    ? 'bg-green-50 border-green-300 text-green-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {diet}
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );

  const renderCurrentStep = () => {
    // For office_staff, insert physician search as step 2
    if (onboardingData.userType === 'office_staff') {
      switch (currentStep) {
        case 1:
          return renderStep1(); // User type selection
        case 2:
          return renderPhysicianSearchStep(); // Physician search (unique to office_staff)
        case 3:
          return renderStep2(); // Profile details
        case 4:
          return renderStep3(); // Location
        default:
          return null;
      }
    }

    // For other user types (sales, manager, customer)
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        // Only show step 4 for healthcare providers
        return onboardingData.userType === 'customer' ? renderStep4() : null;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-blue-600">Step {currentStep} of {getTotalSteps()}</span>
            <span className="text-sm text-gray-500">{Math.round((currentStep / getTotalSteps()) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / getTotalSteps()) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Current Step Content */}
        <div className="mb-8">
          {renderCurrentStep()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors ${
                currentStep === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            
            {/* Emergency Reset Button */}
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to cancel registration and return to login?')) {
                  localStorage.removeItem('repconnect_current_user');
                  localStorage.removeItem('repconnect_sync_queue');
                  localStorage.removeItem('repconnect_sync_status');
                  setCurrentView('login');
                }
              }}
              className="text-red-600 hover:text-red-800 hover:bg-red-50 px-4 py-2 rounded-lg transition-colors text-sm"
            >
              Cancel Registration
            </button>
          </div>

          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors ${
              canProceed()
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <span>{currentStep === getTotalSteps() ? 'Complete Setup' : 'Next'}</span>
            {currentStep === getTotalSteps() ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <ArrowRight className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;